import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { Table } from "reactstrap";
import { GET_AUDIT_ENTITY } from "../../../graphql/log-gql";
import { Pageable, TypedEntity } from "../../../types/common";
import { AuditActionType, AuditChangeType, IAuditLog, IAuditTransaction } from "../../../types/log";
import DateFormat from "../../common/date-format";
import AnimatedPlaceholder from "../../common/animated-placeholder";
import { TreeCollapseIcon, TreeExpandIcon } from "../../common/icons";
import PaginationBar from "../../common/pagination-bar";
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";

const TABLE_HEADERS: TableHeaderInfo[] = [
  {},
  { header: 'Transaction Date' },
  { header: 'Modified By' },
  { header: 'Action' },
  { header: 'Type of Change' },
  { header: 'Impacted Entity' },
  { header: 'DB Field Name' },
  { header: 'New Value' },
  { header: 'Old Value' }
];

const isSameAction = (a: IAuditLog, b: IAuditLog): boolean =>
  AuditActionType[a.actionType] === AuditActionType[b.actionType]
  && a.changeType === b.changeType
  && a.entity.id === b.entity.id
  && a.entity.type === b.entity.type
  && a.entity.name === b.entity.name;

const ActionName = (props: {
  type: string
}) => {
  const isExpired = props.type === 'E' || props.type === 'D';
  const name = AuditActionType[props.type];
  return (isExpired
    ? <span className="audit-expired-action">{name}</span>
    : <>{name}</>
  );
};

const EntityName = (props: {
  value?: string
}) => {
  const values = props.value?.split('|') ?? [];
  return <>
    {values.map((v, index) =>
      <span key={index}>
        {index > 0 && <br />}
        {index === 1 ? '- ' + v + ' -' : v.replace('@', ' of ')}
      </span>
    )}
  </>
};

const ActionInfo = (props: {
  log: IAuditLog;
  showVarious: boolean;
  showAction: boolean;
}) => {
  const { log, showVarious, showAction } = props;

  const [entity, setEntity] = useState(log.entity);

  const [getAuditEntity, { loading }] = useLazyQuery<{ auditEntity: TypedEntity }>(GET_AUDIT_ENTITY);

  useEffect(() => {
    if (!entity.name) {
      getAuditEntity({
        variables: {
          id: entity.id,
          type: entity.type
        }
      }).then(response => response.data?.auditEntity && setEntity(response.data?.auditEntity));
    }
  }, [entity, getAuditEntity]);

  return <>
    <td>
      {showVarious ? 'Various' : showAction &&
        <ActionName type={log.actionType} />
      }
    </td>
    <td>
      {showVarious ? 'Various' : showAction &&
        <>
          {log.changeType && log.entity.type && <>
            {AuditChangeType[log.changeType]} of <AnimatedPlaceholder color="secondary" xs={10} loading={loading}>{entity?.type}</AnimatedPlaceholder>
          </>}
          {log.entity.id > 0 && <>
            <br />ID: {log.entity.id}
          </>}
        </>
      }
    </td>
    <td>
      {showVarious ? 'Various' : showAction &&
        <AnimatedPlaceholder color="secondary" xs={10} loading={loading}>
          <EntityName value={entity.name} />
        </AnimatedPlaceholder>
      }
    </td>
  </>
};

const AuditLogTable = (props: Pageable<IAuditTransaction>) => {
  const { pageSize, data } = props;

  const totalRecords = data?.reduce((n, tx) => n + tx.logs.length, 0);

  const [pagedData, setPagedData] = useState<IAuditTransaction[]>([]);

  const [expandStates, setExpandStates] = useState<Map<string, boolean>>();

  const toggle = useCallback((trxId: string) => {
    const newMap = new Map(expandStates);
    newMap.set(trxId, !expandStates?.get(trxId));
    setExpandStates(newMap);
  }, [expandStates]);

  return (
    <section aria-label="Audit log search results">
      <Table striped hover className="audit-logs">
        <caption className="sr-only">OMS transactions. Changes that affect multiple fields will be expandable</caption>
        <TableHeaders headers={TABLE_HEADERS} />

        {data && data.length > 0
          ?
          <tbody>
            {pagedData.map((item) => {
              const hasVariousActions = item.logs.length > 1
                && item.logs.some(log => !isSameAction(log, item.logs[0]));

              return item.logs.map((log, index) => {
                const isNewRecord = index === 0;
                const isExpanded = !!expandStates?.get(item.trxId);

                if (!isNewRecord && !isExpanded) return <></>;

                const isToggle = isNewRecord && item.logs.length > 1;
                const isToggleCollapsed = isToggle && !isExpanded;
                const showAction = isNewRecord || !isSameAction(log, item.logs[index - 1]);

                return (
                  <tr key={log.id}
                    className={isToggle ? 'pe-force' : ''}
                    tabIndex={isToggle ? 0 : -1}
                    onClick={() => isToggle && toggle(item.trxId)}
                    onKeyUp={(e) => (e.key === 'Enter' || e.key === ' ') && isToggle && toggle(item.trxId)}
                    aria-expanded={isExpanded}
                  >
                    <td className="small">
                      {isNewRecord && isToggle && (isExpanded ? <TreeCollapseIcon /> : <TreeExpandIcon />)}
                    </td>
                    <td>
                      {isNewRecord && <>
                        <DateFormat date={log.ts} format="MMMM D, YYYY HH:mm:ss" />
                        <br />
                        TrxID: {item.trxId}
                      </>}
                    </td>
                    <td>
                      {isNewRecord &&
                        <>{item.user?.firstName && item.user?.lastName
                          ? <>{item.user.lastName}, {item.user.firstName}<br />({item.userId})</>
                          : item.userId
                        }</>
                      }
                    </td>
                    <ActionInfo log={log} showVarious={isToggleCollapsed && hasVariousActions} showAction={showAction} />
                    <td>{isToggleCollapsed ? 'Various' : log.field}</td>
                    <td>{isToggleCollapsed ? 'Various' : log.newValue}</td>
                    <td>{isToggleCollapsed ? 'Various' : log.oldValue}</td>
                  </tr>
                );
              });
            })}
          </tbody>
          :
          <tfoot>
            <tr>
              <td colSpan={TABLE_HEADERS.length} className="text-center">
                {data?.length === 0
                  ? 'No audit logs found.'
                  : 'Search results will appear in this table.'
                }
              </td>
            </tr>
          </tfoot>
        }
      </Table>

      <PaginationBar data={data} pageSize={pageSize} onPageChange={setPagedData}>
        {!!data?.length && <span className="ms-5"><b>Total Records:</b> {totalRecords}</span>}
      </PaginationBar>
    </section>
  );
};

export default AuditLogTable;
